import {isFeatureEnabled} from 'feature-flags';
import {Option} from 'platform/components';
import {useLocale} from 'platform/locale';

import {useCallback, useMemo} from 'react';

import {isNil, isNotNil, last, length, omit, sort} from 'ramda';

import {usePriceReport} from '../../../hooks/usePriceReport';
import {useDrillDownQuery} from '../../../store/omneticApi';
import {DrillDownLevelName} from '../../../types/api/drillDown';
import {featureFlags} from '../../../utils/featureFlags';
import {getPermanentLevelFilters} from '../../DrillDown/components/DrillDown/utils/getPermanentLevelFilters';

const useGetCounts = <T extends string | number>(
  optionKey: DrillDownLevelName,
  filters: Partial<Record<DrillDownLevelName, T>>,
  market?: string
) => {
  const {language} = useLocale();
  const {requestData} = usePriceReport();
  const resolvedMarket = isNotNil(market) ? market : requestData?.market;
  const levelFilters = getPermanentLevelFilters(omit([optionKey], filters));
  const hasFilters =
    isNotNil(filters.make) &&
    isNotNil(filters.model) &&
    isFeatureEnabled(featureFlags.PR_FORM_STATISTICS);
  const {data: drillDownGlobalData, isLoading: isLoadingGlobal} = useDrillDownQuery(
    {
      levels: [
        ...levelFilters,
        {
          level: optionKey,
          values: [],
          priority: length(levelFilters) + 1,
        },
      ],
      currency: 'CZK', // We don't know currency, but we are not going to use prices
      regions: [],
      lang: language,
    },
    {skip: !hasFilters}
  );
  const {data: drillDownMarketData, isLoading: isLoadingMarket} = useDrillDownQuery(
    {
      levels: [
        ...levelFilters,
        {
          level: optionKey,
          values: [],
          priority: length(levelFilters) + 1,
        },
      ],
      currency: 'CZK', // We don't know currency, but we are not going to use prices
      regions: isNotNil(resolvedMarket) ? [resolvedMarket] : [],
      lang: language,
    },
    {skip: !hasFilters}
  );

  const counts = useMemo(
    () =>
      drillDownGlobalData?.statisticsAggregated.map((item) => {
        const option = last(item.levels)?.value;
        const marketStatistics = drillDownMarketData?.statisticsAggregated.find(
          (row) => last(row.levels)?.value === option
        )?.statistics;

        return {
          option,
          count: marketStatistics?.reduce((acc, curr) => acc + curr.amount, 0) ?? 0,
        };
      }) ?? [],
    [drillDownGlobalData, drillDownMarketData]
  );

  return {counts, isLoading: isLoadingGlobal || isLoadingMarket};
};

export interface FilterRelevantOptionsResult<T extends string | number> {
  options: (Option<T> & {suffix?: string})[];
  isLoading: boolean;
}

export const useFilterRelevantOptions = <T extends string | number>(
  options: Option<T>[],
  optionKey: DrillDownLevelName,
  filters: Partial<Record<DrillDownLevelName, string>>,
  market?: string
): FilterRelevantOptionsResult<T> => {
  const {counts, isLoading} = useGetCounts(optionKey, filters, market);
  const getCount = useCallback(
    (option: Option<T>) => counts.find((count) => count.option === option.value.toString())?.count,
    [counts]
  );

  const mappedOptions = useMemo(
    () =>
      options
        .filter((item) => {
          const count = getCount(item);
          return isNotNil(count); // Values we have in data, including those which does not exist in the selected market
        })
        .map((item) => ({
          ...item,
          suffix: `(${getCount(item)})`,
          isDisabled: false,
        })),
    [options, getCount]
  );

  if (
    isNil(filters.make) ||
    isNil(filters.model) ||
    !isFeatureEnabled(featureFlags.PR_FORM_STATISTICS)
  ) {
    return {options, isLoading: false};
  }

  return {options: mappedOptions, isLoading};
};

export const useGetRelevantPowerOptions = (
  filters: Partial<Record<DrillDownLevelName, string>>,
  market?: string
): FilterRelevantOptionsResult<string> => {
  const {counts, isLoading} = useGetCounts('power', filters, market);

  const options = useMemo(
    () =>
      sort((a, b) => Number(a.option) - Number(b.option), counts).map((count) => ({
        value: count.option ?? '?',
        label: `${count.option} kW`,
        suffix: `(${count.count})`,
      })),
    [counts]
  );

  if (
    isNil(filters.make) ||
    isNil(filters.model) ||
    !isFeatureEnabled(featureFlags.PR_FORM_STATISTICS)
  ) {
    return {options: [], isLoading: false};
  }

  return {options, isLoading};
};
